import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Instagram, Facebook } from "lucide-react";
import { BRAND, SERVICES, CONTACT } from "../../data/site";
import { track, EVENTS } from "../../lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NAV = [
  { label: "How It Works", to: "/how-it-works" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "border-b border-seafoam/60 bg-cream/90 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="container-hh flex items-center justify-between px-6 py-4 md:px-10 lg:px-16">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`flex items-center gap-3 transition-opacity duration-300 ${
            scrolled ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-label="Harbor Home Concierge home"
        >
          <img
            src={BRAND.logoMark}
            alt="Harbor Home Concierge"
            className="h-11 w-auto md:h-12"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-7 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-services-trigger"
              className="flex items-center gap-1 text-sm font-medium text-navy outline-none transition-colors hover:text-teal"
            >
              Services <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-64 rounded-[12px] border-seafoam bg-white p-2 shadow-cozy-lg"
            >
              {SERVICES.map((s) => (
                <DropdownMenuItem key={s.slug} asChild>
                  <Link
                    to={s.path}
                    data-testid={`nav-service-${s.slug}`}
                    className="cursor-pointer rounded-[8px] px-3 py-2 text-sm text-bodytext focus:bg-seafoam focus:text-navy"
                  >
                    {s.title}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-teal ${
                  isActive ? "text-teal" : "text-navy"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          <div className="flex items-center gap-2">
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              onClick={() => track(EVENTS.INSTAGRAM_CLICK, { location: "navbar" })}
              aria-label="Instagram"
              className="flex h-9 w-9 items-center justify-center rounded-full text-navy transition-colors hover:bg-seafoam hover:text-teal"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-facebook"
              aria-label="Facebook"
              className="flex h-9 w-9 items-center justify-center rounded-full text-navy transition-colors hover:bg-seafoam hover:text-teal"
            >
              <Facebook className="h-5 w-5" />
            </a>
          </div>

          <Link to="/book" data-testid="nav-book-btn" className="btn-teal">
            Book Your Free Walkthrough
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="text-navy lg:hidden"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="border-t border-seafoam bg-cream px-6 py-5 lg:hidden"
        >
          <p className="eyebrow mb-2">Services</p>
          <div className="mb-4 flex flex-col gap-1">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={s.path}
                data-testid={`mnav-service-${s.slug}`}
                className="rounded-[8px] py-2 text-bodytext"
              >
                {s.title}
              </Link>
            ))}
          </div>
          <div className="flex flex-col gap-1 border-t border-seafoam pt-4">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mnav-${item.to.replace("/", "")}`}
                className="py-2 font-medium text-navy"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <Link
            to="/book"
            data-testid="mnav-book-btn"
            className="btn-teal mt-4 w-full"
          >
            Book Your Free Walkthrough
          </Link>
          <div className="mt-4 flex items-center gap-3 border-t border-seafoam pt-4">
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mnav-instagram"
              aria-label="Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-seafoam text-navy"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mnav-facebook"
              aria-label="Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-seafoam text-navy"
            >
              <Facebook className="h-5 w-5" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
